import React from 'react'
import { ExternalLink, Book } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

interface BibleVerseLinkProps {
  reference: string // e.g., "John 3:16" or "Genesis 1:1-3"
  children?: React.ReactNode
  variant?: 'inline' | 'button' | 'badge'
  className?: string
}

/**
 * Component that creates clickable Bible verse links that open in Bible apps
 *
 * @example
 * <BibleVerseLink reference="John 3:16" />
 * <BibleVerseLink reference="Psalm 23:1-6" variant="button" />
 * <BibleVerseLink reference="Romans 8:28">For we know...</BibleVerseLink>
 */
export function BibleVerseLink({
  reference,
  children,
  variant = 'inline',
  className = ''
}: BibleVerseLinkProps) {
  // Format reference for URLs (replace spaces with +, handle special characters)
  const formatForUrl = (ref: string) => {
    return encodeURIComponent(ref.trim())
  }

  const urlReference = formatForUrl(reference)

  // Generate URLs for different Bible apps/sites
  const bibleUrls = {
    youversion: `https://www.bible.com/search/bible?q=${urlReference}`,
    bibleGateway: `https://www.biblegateway.com/passage/?search=${urlReference}&version=NIV`,
    blueLetterBible: `https://www.blueletterbible.org/search/search.cfm?Criteria=${urlReference}&t=NIV`,
    esv: `https://www.esv.org/${urlReference}`,
  }

  // For mobile deep links (YouVersion app)
  const youversionDeepLink = `bible://${urlReference}`

  const handleOpenBible = (url: string, useDeepLink = false) => {
    if (useDeepLink) {
      // Try to open the app, fallback to web
      window.location.href = youversionDeepLink
      setTimeout(() => {
        window.open(bibleUrls.youversion, '_blank')
      }, 500)
    } else {
      window.open(url, '_blank', 'noopener,noreferrer')
    }
  }

  if (variant === 'inline') {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className={`inline-flex items-center gap-1 text-primary hover:text-primary/80 hover:underline font-medium cursor-pointer ${className}`}
          >
            <Book className="h-3 w-3" />
            {children || reference}
            <ExternalLink className="h-3 w-3" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuItem onClick={() => handleOpenBible(bibleUrls.youversion, true)}>
            <Book className="mr-2 h-4 w-4" />
            YouVersion Bible App
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleOpenBible(bibleUrls.bibleGateway)}>
            <ExternalLink className="mr-2 h-4 w-4" />
            Bible Gateway (Web)
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleOpenBible(bibleUrls.blueLetterBible)}>
            <ExternalLink className="mr-2 h-4 w-4" />
            Blue Letter Bible
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleOpenBible(bibleUrls.esv)}>
            <ExternalLink className="mr-2 h-4 w-4" />
            ESV.org
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    )
  }

  if (variant === 'button') {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className={className}>
            <Book className="mr-2 h-4 w-4" />
            {children || reference}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuItem onClick={() => handleOpenBible(bibleUrls.youversion, true)}>
            <Book className="mr-2 h-4 w-4" />
            YouVersion Bible App
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleOpenBible(bibleUrls.bibleGateway)}>
            <ExternalLink className="mr-2 h-4 w-4" />
            Bible Gateway (Web)
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleOpenBible(bibleUrls.blueLetterBible)}>
            <ExternalLink className="mr-2 h-4 w-4" />
            Blue Letter Bible
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleOpenBible(bibleUrls.esv)}>
            <ExternalLink className="mr-2 h-4 w-4" />
            ESV.org
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    )
  }

  if (variant === 'badge') {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md bg-primary/10 text-primary hover:bg-primary/20 cursor-pointer ${className}`}
          >
            <Book className="h-3 w-3" />
            {children || reference}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuItem onClick={() => handleOpenBible(bibleUrls.youversion, true)}>
            <Book className="mr-2 h-4 w-4" />
            YouVersion Bible App
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleOpenBible(bibleUrls.bibleGateway)}>
            <ExternalLink className="mr-2 h-4 w-4" />
            Bible Gateway (Web)
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleOpenBible(bibleUrls.blueLetterBible)}>
            <ExternalLink className="mr-2 h-4 w-4" />
            Blue Letter Bible
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleOpenBible(bibleUrls.esv)}>
            <ExternalLink className="mr-2 h-4 w-4" />
            ESV.org
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    )
  }

  return null
}

/**
 * Parse text and automatically convert Bible verse references to clickable links
 *
 * @example
 * <BibleVerseText text="Read John 3:16 and Romans 8:28 for encouragement" />
 */
export function BibleVerseText({ text }: { text: string }) {
  // Regex to match Bible verse references
  // Matches patterns like: "John 3:16", "1 Corinthians 13:4-7", "Psalm 23:1-6"
  const bibleRefRegex = /\b(\d?\s?[A-Z][a-z]+(?:\s+[A-Z][a-z]+)?)\s+(\d+):(\d+)(?:-(\d+))?\b/g

  const parts: (string | React.ReactNode)[] = []
  let lastIndex = 0
  let match

  while ((match = bibleRefRegex.exec(text)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index))
    }

    // Add the Bible verse link
    const reference = match[0]
    parts.push(
      <BibleVerseLink key={match.index} reference={reference} variant="inline" />
    )

    lastIndex = match.index + match[0].length
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex))
  }

  return <>{parts}</>
}
